import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { PlayerScaffold, PlayerFooter } from '@/components/player/PlayerScaffold';
import { TabStrip, type Tab } from '@/components/player/TabStrip';
import { Question } from '@/components/quiz/Question';
import { OptionState, type OptionCss } from '@/components/quiz/types';
import type { EducateModule } from '@/hooks/useEducateModule';
import type { EducateUnit } from '@/utils/units';
import type { ScenarioLedContent, QuizQuestion } from '@/types/educate';
import { loadPlayerProgress, savePlayerProgress } from '@/utils/playerProgress';

interface ScenarioSaved {
  picked: number | null;
  submitted: boolean;
}

const RESOLUTION_ID = 'resolution';
const SITUATION_ID = 'situation';

const SCENARIO_OPTION_CSS: OptionCss = {
  base: 'flex w-full items-start gap-3 rounded-xl border px-5 py-4 text-left text-[15px] leading-relaxed transition disabled:cursor-default',
  state: {
    [OptionState.DEFAULT]: 'border-slate-200 bg-white text-slate-700 hover:border-brand-400',
    [OptionState.SELECTED]: 'border-brand-500 bg-brand-50 font-medium text-brand-900',
    [OptionState.CORRECT]: 'border-emerald-500 bg-emerald-50 text-emerald-900',
    [OptionState.INCORRECT]: 'border-red-400 bg-red-50 text-red-600',
    [OptionState.MUTED]: 'border-slate-200 bg-white text-slate-400'
  }
};

function tabLabel(unit: EducateUnit, moveNumber: number): string {
  if (unit.id === SITUATION_ID) return 'Brief';
  if (unit.id === RESOLUTION_ID) return 'Outcome';
  if (unit.kind === 'check') return 'Your turn';
  if (unit.kind === 'beat') return `Move ${moveNumber}`;
  return unit.label;
}

export function ScenarioPlayer({ module }: { module: EducateModule }) {
  const { storyline, units, currentUnit, currentIndex, completedUnits, advance, goBack, goTo } =
    module;
  const content = storyline?.content as ScenarioLedContent;
  const showLogo = storyline?.uiContent.uiConfig?.showKnolskapeLogo ?? true;
  const total = units.length;

  const saved = useMemo(
    () => loadPlayerProgress<ScenarioSaved>(storyline?.storylineId),
    [storyline?.storylineId]
  );
  const [picked, setPicked] = useState<number | null>(() =>
    typeof saved.picked === 'number' ? saved.picked : null
  );
  const [submitted, setSubmitted] = useState(() => Boolean(saved.submitted));

  useEffect(() => {
    savePlayerProgress<ScenarioSaved>(storyline?.storylineId, { picked, submitted });
  }, [storyline?.storylineId, picked, submitted]);

  const firstIncomplete = units.findIndex((unit) => !completedUnits.includes(unit.id));
  const furthest = firstIncomplete === -1 ? total - 1 : firstIncomplete;

  let moveCounter = 0;
  const tabs: Tab[] = units.map((unit, index) => {
    if (unit.kind === 'beat') moveCounter += 1;
    const label = tabLabel(unit, moveCounter);
    const state =
      index === currentIndex
        ? 'active'
        : completedUnits.includes(unit.id)
          ? 'done'
          : index <= furthest
            ? 'default'
            : 'locked';
    return { id: unit.id, label, state };
  });

  if (!currentUnit) return null;

  const onCheck = currentUnit.kind === 'check';
  const canSubmit = picked !== null;
  const canFinish = !onCheck || submitted;

  const handleForward = () => {
    if (onCheck) {
      if (!submitted) {
        setSubmitted(true);
        return;
      }
      advance([
        {
          unitId: currentUnit.id,
          kind: 'quiz',
          answer: picked ?? -1,
          isCorrect: picked === currentUnit.question.correctIndex
        }
      ]);
    } else {
      advance();
    }
  };

  return (
    <PlayerScaffold
      eyebrow="Scenario-Led Learning"
      title={storyline?.uiContent.title ?? ''}
      showLogo={showLogo}
      setting={content?.setting}
      cast={content?.cast}
      bodyTestId="scenario-screen"
      nav={<TabStrip tabs={tabs} onSelect={goTo} />}
      footer={
        <PlayerFooter
          left={
            <button
              type="button"
              onClick={goBack}
              disabled={currentIndex <= 0}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 disabled:opacity-40"
            >
              <ArrowLeft size={16} /> Back
            </button>
          }
          center={
            <>
              {onCheck && !submitted
                ? canSubmit
                  ? 'Submit to see the answer · '
                  : 'Choose an answer · '
                : ''}
              Step {currentIndex + 1} of {total}
            </>
          }
          right={
            <button
              type="button"
              onClick={handleForward}
              disabled={onCheck && !submitted ? !canSubmit : !canFinish}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-brand-700 disabled:opacity-40"
            >
              {onCheck ? (submitted ? 'Finish scenario' : 'Submit answer') : 'Continue'}
              {!onCheck && <ArrowRight size={16} />}
            </button>
          }
        />
      }
    >
      <StageBody
        unit={currentUnit}
        stageLabel={tabs[currentIndex]?.label ?? ''}
        picked={picked}
        submitted={submitted}
        onPick={setPicked}
      />
    </PlayerScaffold>
  );
}

interface StageBodyProps {
  unit: EducateUnit;
  stageLabel: string;
  picked: number | null;
  submitted: boolean;
  onPick: (index: number) => void;
}

function StageBody({ unit, stageLabel, picked, submitted, onPick }: StageBodyProps) {
  if (unit.kind === 'check') {
    return (
      <YourTurn question={unit.question} picked={picked} submitted={submitted} onPick={onPick} />
    );
  }

  const eyebrow =
    unit.id === SITUATION_ID ? 'The brief' : unit.id === RESOLUTION_ID ? 'The outcome' : stageLabel;
  const heading = unit.kind === 'beat' ? unit.point : unit.title;
  const body = unit.kind === 'beat' ? unit.explanation : unit.kind === 'prose' ? unit.body : '';
  const accent = unit.id === SITUATION_ID;

  return (
    <article>
      <p className="text-xs font-bold uppercase tracking-[0.16em] text-brand-600">{eyebrow}</p>
      <h2 className="mt-2 text-2xl font-bold text-slate-900">{heading}</h2>
      <div
        className={`mt-5 rounded-xl bg-white p-6 shadow-sm ${
          accent ? 'border-l-4 border-amber-400' : 'border border-slate-200'
        }`}
      >
        <p className="whitespace-pre-line text-lg leading-relaxed text-slate-700">{body}</p>
      </div>
    </article>
  );
}

function YourTurn({
  question,
  picked,
  submitted,
  onPick
}: {
  question: QuizQuestion;
  picked: number | null;
  submitted: boolean;
  onPick: (index: number) => void;
}) {
  const correct = question.correctIndex;

  const optionState = (index: number): OptionState => {
    if (!submitted) return index === picked ? OptionState.SELECTED : OptionState.DEFAULT;
    if (index === picked) return picked === correct ? OptionState.CORRECT : OptionState.INCORRECT;
    if (index === correct) return OptionState.CORRECT;
    return OptionState.MUTED;
  };

  return (
    <article>
      <p className="text-xs font-bold uppercase tracking-[0.16em] text-brand-600">
        Now you respond
      </p>
      <h2 className="mt-2 text-2xl font-bold text-slate-900">Put it in your own words</h2>

      <div className="mt-5 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <Question
          question={question.question}
          options={question.options}
          optionState={optionState}
          onSelect={(index) => !submitted && onPick(index)}
          isDisabled={() => submitted}
          showLetters
          title={
            <p className="text-lg font-bold leading-snug text-slate-900">{question.question}</p>
          }
          css={{ title: '', list: 'mt-5 space-y-3', option: SCENARIO_OPTION_CSS }}
          feedback={
            submitted && question.feedback ? (
              <p className="mt-5 whitespace-pre-line border-t-2 border-slate-200 pt-4 text-base leading-relaxed text-slate-600">
                {question.feedback}
              </p>
            ) : null
          }
        />
      </div>
    </article>
  );
}
